import React, { useMemo, useState } from 'react';
import {
  Camera,
  Crown,
  Eye,
  Flame,
  MoreHorizontal,
  Radio,
  Search,
  Shuffle,
  Skull,
  Sparkles,
  Trash2,
  Upload,
  Users,
} from 'lucide-react';
import {
  focalPointToCssPosition,
  type BattlefieldRow,
  type ClientSeatView,
  type GameIntent,
  type InteractionFlag,
  type PublicCardView,
} from '#shared';
import { CardSleeveBack, CardVisual } from './CardVisual.tsx';
import { HexLifeBadge } from './HexLifeBadge.tsx';

interface PlayerBoardProps {
  seat: ClientSeatView;
  isMe: boolean;
  mirrored?: boolean;
  isActiveTurn: boolean;
  seatFlag: InteractionFlag | undefined;
  useCommandZone: boolean;
  compact?: boolean;
  reducedMotion: boolean;
  onDispatch: (intent: GameIntent) => void;
  onInspectCard: (card: PublicCardView) => void;
  onOpenCardMenu: (card: PublicCardView, e: React.MouseEvent) => void;
  onOpenZoneBrowser: (seatId: string, zone: 'graveyard' | 'exile' | 'sideboard') => void;
  onOpenDeckLoader?: () => void;
  onOpenCosmetics?: () => void;
  onOpenPaperMode?: () => void;
  lowBandwidthStillUrl?: string | null;
  onOpenGrantLibraryAccess?: () => void;
  onOpenHelpers?: (
    tab: 'tokens' | 'commander' | 'counters' | 'designations' | 'cascade' | 'notes'
  ) => void;
}

interface StackedGroup {
  key: string;
  items: PublicCardView[];
}

function groupIdenticalPermanents(cards: PublicCardView[]): StackedGroup[] {
  const groups = new Map<string, PublicCardView[]>();
  for (const item of cards) {
    const canStack =
      !item.faceDown &&
      !item.attachedToId &&
      Object.keys(item.counters).length === 0 &&
      item.card !== null;

    const key = canStack
      ? `stack:${item.card!.name}:${item.tapped ? 'T' : 'U'}:${item.flipped ? 'F' : 'N'}`
      : `single:${item.instanceId}`;

    const arr = groups.get(key) ?? [];
    arr.push(item);
    groups.set(key, arr);
  }
  return Array.from(groups.entries()).map(([key, items]) => ({ key, items }));
}

/**
 * Ultra PRO Two-Player Playmat × MTG Arena Battlefield:
 * - Bottom player (`mirrored=false`):
 *     Left Rail: Hex Life Total + Command + Exile
 *     Center Framed Mat: Creatures/Permanents (top half) | hairline divider | Lands (bottom half)
 *     Right Rail: Library (top) + Graveyard (bottom)
 * - Top opponent (`mirrored=true`):
 *     Left Rail: Graveyard (top) + Library (bottom)
 *     Center Framed Mat: Lands (top half) | hairline divider | Creatures/Permanents (bottom half)
 *     Right Rail: Exile + Command (top) + Hex Life Total (bottom)
 */
export function PlayerBoard({
  seat,
  isMe,
  mirrored = false,
  isActiveTurn,
  seatFlag,
  useCommandZone,
  compact = false,
  reducedMotion,
  onDispatch,
  onInspectCard,
  onOpenCardMenu,
  onOpenZoneBrowser,
  onOpenDeckLoader,
  onOpenCosmetics,
  onOpenPaperMode,
  lowBandwidthStillUrl,
  onOpenGrantLibraryAccess,
  onOpenHelpers,
}: PlayerBoardProps) {
  const [libraryMenuOpen, setLibraryMenuOpen] = useState(false);
  const [libraryCountInput, setLibraryCountInput] = useState(1);
  const [expandedGroupKey, setExpandedGroupKey] = useState<string | null>(null);
  const [tokenPopoverOpen, setTokenPopoverOpen] = useState(false);
  const [seatMenuOpen, setSeatMenuOpen] = useState(false);

  const grouped = useMemo(() => {
    const creaturesAndPerms: PublicCardView[] = [];
    const lands: PublicCardView[] = [];
    for (const c of seat.zones.battlefield) {
      if ((c.row ?? 'other') === 'lands') lands.push(c);
      else creaturesAndPerms.push(c);
    }
    return {
      frontLine: groupIdenticalPermanents(creaturesAndPerms),
      landsLine: groupIdenticalPermanents(lands),
    };
  }, [seat.zones.battlefield]);

  const cardSize = compact ? 'xs' : 'sm';

  const lifeAndExileRail = (
    <div className={`flex ${mirrored ? 'flex-col-reverse' : 'flex-col'} items-center justify-between gap-2`}>
      <HexLifeBadge
        seatId={seat.seatId}
        seatName={seat.displayName}
        life={seat.life}
        profileColor={seat.profileColor}
        playerCounters={seat.playerCounters}
        canEdit={isMe}
        onCommitLifeDelta={(delta) =>
          onDispatch({ type: 'ADJUST_LIFE', targetSeatId: seat.seatId, delta })
        }
        onAdjustCounter={(counter, delta) =>
          onDispatch({ type: 'ADJUST_PLAYER_COUNTER', targetSeatId: seat.seatId, counter, delta })
        }
      />

      {useCommandZone && (
        <div data-zone-anchor={`${seat.seatId}:command`} className="flex flex-col items-center gap-1">
          {seat.zones.command.length > 0 ? (
            seat.zones.command.map((cmd) => {
              const tax = seat.commanderTax[cmd.instanceId] ?? 0;
              return (
                <div key={cmd.instanceId} className="relative">
                  <CardVisual
                    item={cmd}
                    size="xs"
                    reducedMotion={reducedMotion}
                    onClick={() => {
                      if (isMe) {
                        onDispatch({ type: 'MOVE_CARD', instanceId: cmd.instanceId, toZone: 'battlefield' });
                      } else {
                        onInspectCard(cmd);
                      }
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      onOpenCardMenu(cmd, e);
                    }}
                    onInspect={() => onInspectCard(cmd)}
                  />
                  {tax > 0 && (
                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-1 rounded bg-amber-950 border border-amber-400 text-[8px] font-bold text-amber-200 whitespace-nowrap">
                      +{tax * 2} Tax
                    </span>
                  )}
                </div>
              );
            })
          ) : (
            <div className="w-14 h-12 rounded-md border border-white/15 bg-slate-950/50 flex flex-col items-center justify-center text-[8px] uppercase tracking-wider text-slate-400">
              <Crown className="w-3 h-3 text-amber-400/70 mb-0.5" />
              <span>Cmd</span>
            </div>
          )}
        </div>
      )}

      {/* EXILE Box */}
      <button
        type="button"
        data-zone-anchor={`${seat.seatId}:exile`}
        onClick={() => onOpenZoneBrowser(seat.seatId, 'exile')}
        aria-label={`Exile (${seat.zones.exile.length} cards)`}
        className="w-14 h-16 rounded-md bg-purple-950/25 border border-white/20 hover:border-purple-400/80 flex flex-col items-center justify-center text-[10px] text-purple-200"
      >
        <Flame className="w-3.5 h-3.5 text-purple-400 mb-0.5" />
        <span className="font-bold">{seat.zones.exile.length}</span>
        <span className="text-[8px] uppercase tracking-wider text-purple-300/80">Exile</span>
      </button>
    </div>
  );

  const libraryAndGraveRail = (
    <div className={`flex ${mirrored ? 'flex-col-reverse' : 'flex-col'} items-center justify-between gap-2`}>
      {/* LIBRARY Box */}
      <div
        className={`relative flex flex-col items-center gap-0.5 ${libraryMenuOpen ? 'z-[85]' : 'z-10'}`}
        data-zone-anchor={`${seat.seatId}:library`}
      >
        <span className="text-[7px] font-bold uppercase tracking-wider text-slate-400 select-none">
          Library
        </span>
        <button
          type="button"
          disabled={!isMe}
          onClick={() => isMe && setLibraryMenuOpen((v) => !v)}
          aria-label={`Library (${seat.zones.libraryCount} cards)`}
          className="relative p-0.5 rounded-md border border-white/20 hover:border-amber-400/70 transition-colors focus:outline-none"
        >
          {seat.zones.libraryTopCard?.card ? (
            <CardVisual item={seat.zones.libraryTopCard} accentColor={seat.profileColor} size="xs" reducedMotion={reducedMotion} />
          ) : (
            <CardSleeveBack sleeveUrl={seat.sleeveUrl} accentColor={seat.profileColor} size="xs" />
          )}
          <span className="absolute -bottom-1.5 -right-1.5 px-1.5 py-0.5 rounded-full bg-slate-950 border border-amber-400/80 text-amber-300 font-extrabold text-[10px]">
            {seat.zones.libraryCount}
          </span>
        </button>

        {isMe && libraryMenuOpen && (
          <div
            style={{ backgroundColor: '#0b101d' }}
            className="absolute right-16 top-0 z-[95] w-56 rounded-2xl bg-[#0b101d] border border-slate-600 shadow-[0_24px_60px_rgba(0,0,0,0.95)] p-2 text-xs flex flex-col gap-1"
          >
            <div className="px-1.5 py-1 flex items-center justify-between border-b border-slate-800/80 mb-0.5">
              <span className="font-bold text-slate-100">Library</span>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-400">{seat.zones.libraryCount} cards</span>
                <button type="button" onClick={() => setLibraryMenuOpen(false)} className="text-slate-400 hover:text-white">
                  ✕
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-1.5 pb-1">
              <button
                type="button"
                onClick={() => {
                  onDispatch({ type: 'DRAW_CARDS', count: 1 });
                  setLibraryMenuOpen(false);
                }}
                className="px-2.5 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-400 text-slate-950 font-extrabold flex items-center justify-between"
              >
                <span>Draw</span>
                <span className="text-[10px] opacity-80">D</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  onDispatch({ type: 'SHUFFLE_LIBRARY' });
                  setLibraryMenuOpen(false);
                }}
                className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-100 font-bold flex items-center justify-between"
              >
                <span className="flex items-center gap-1">
                  <Shuffle className="w-3 h-3" />
                  <span>Shuffle</span>
                </span>
                <span className="text-[10px] text-slate-400">R</span>
              </button>
            </div>

            {/* Top N Cards Stepper (`[-][ N ][+]` — Look / Scry / Mill / Draw) */}
            <div className="p-2 rounded-xl bg-slate-900/90 border border-slate-800 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-bold text-slate-300">Top cards</span>
                <div className="flex items-center rounded-lg bg-slate-950 border border-slate-700 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setLibraryCountInput((n) => Math.max(1, n - 1))}
                    className="px-2 py-0.5 hover:bg-slate-800 text-slate-300 font-bold"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min={1}
                    max={Math.max(1, seat.zones.libraryCount)}
                    value={libraryCountInput}
                    onChange={(e) =>
                      setLibraryCountInput(
                        Math.max(1, Math.min(Math.max(1, seat.zones.libraryCount), Number(e.target.value) || 1))
                      )
                    }
                    className="w-9 text-center bg-transparent text-xs font-bold text-white focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setLibraryCountInput((n) => Math.min(Math.max(1, seat.zones.libraryCount), n + 1))
                    }
                    className="px-2 py-0.5 hover:bg-slate-800 text-slate-300 font-bold"
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1">
                <button
                  type="button"
                  onClick={() => {
                    onDispatch({ type: 'LOOK_TOP_START', count: libraryCountInput });
                    setLibraryMenuOpen(false);
                  }}
                  className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-left text-[11px]"
                >
                  Look ({libraryCountInput})
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onDispatch({ type: 'SCRY_START', count: libraryCountInput });
                    setLibraryMenuOpen(false);
                  }}
                  className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-left text-[11px]"
                >
                  Scry ({libraryCountInput})
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onDispatch({ type: 'MILL_CARDS', count: libraryCountInput });
                    setLibraryMenuOpen(false);
                  }}
                  className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-left text-[11px]"
                >
                  Mill ({libraryCountInput})
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onDispatch({ type: 'DRAW_CARDS', count: libraryCountInput });
                    setLibraryMenuOpen(false);
                  }}
                  className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-left text-[11px]"
                >
                  Draw ({libraryCountInput})
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                onDispatch({ type: 'SEARCH_LIBRARY_START' });
                setLibraryMenuOpen(false);
              }}
              className="px-2 py-1.5 rounded-lg hover:bg-slate-800 text-left text-slate-100 flex items-center justify-between"
            >
              <span className="flex items-center gap-2">
                <Search className="w-3.5 h-3.5 text-cyan-400" />
                <span>Search library</span>
              </span>
              <span className="text-[10px] text-slate-500">V</span>
            </button>
            <button
              type="button"
              onClick={() => {
                onDispatch({ type: 'DRAW_CARDS', count: 1, fromBottom: true });
                setLibraryMenuOpen(false);
              }}
              className="px-2 py-1.5 rounded-lg hover:bg-slate-800 text-left text-slate-200"
            >
              Draw from bottom
            </button>
            {onOpenHelpers && (
              <button
                type="button"
                onClick={() => {
                  setLibraryMenuOpen(false);
                  onOpenHelpers('cascade');
                }}
                className="px-2 py-1.5 rounded-lg hover:bg-slate-800 text-left text-cyan-300 font-semibold"
              >
                Cascade / Reveal until...
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                onDispatch({ type: 'REVEAL_TOP_CARD' });
                setLibraryMenuOpen(false);
              }}
              className="px-2 py-1.5 rounded-lg hover:bg-slate-800 text-left text-slate-200 border-t border-slate-800/80 mt-0.5 pt-1.5"
            >
              Reveal top card
            </button>
            <button
              type="button"
              onClick={() => {
                onDispatch({
                  type: 'TOGGLE_PLAY_TOP_REVEALED',
                  enabled: !seat.playWithTopRevealed,
                });
                setLibraryMenuOpen(false);
              }}
              className="px-2 py-1.5 rounded-lg hover:bg-slate-800 text-left text-slate-200"
            >
              {seat.playWithTopRevealed ? 'Hide top card' : 'Play w/ top card revealed'}
            </button>
            {seat.hasGrantedLibraryAccessTo ? (
              <button
                type="button"
                onClick={() => {
                  onDispatch({ type: 'REVOKE_LIBRARY_ACCESS', ownerSeatId: seat.seatId });
                  setLibraryMenuOpen(false);
                }}
                className="px-2 py-1.5 rounded-lg bg-emerald-950/60 hover:bg-emerald-900 border border-emerald-500/40 text-left text-emerald-200 font-bold border-t mt-0.5"
              >
                End granted library access
              </button>
            ) : (
              onOpenGrantLibraryAccess && (
                <button
                  type="button"
                  onClick={() => {
                    setLibraryMenuOpen(false);
                    onOpenGrantLibraryAccess();
                  }}
                  className="px-2 py-1.5 rounded-lg hover:bg-slate-800 text-left text-slate-200 border-t border-slate-800/80 mt-0.5 pt-1.5 flex items-center gap-2"
                >
                  <Users className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Grant library access...</span>
                </button>
              )
            )}
          </div>
        )}
      </div>

      {/* GRAVEYARD Box */}
      <button
        type="button"
        data-zone-anchor={`${seat.seatId}:graveyard`}
        onClick={() => onOpenZoneBrowser(seat.seatId, 'graveyard')}
        aria-label={`Graveyard (${seat.zones.graveyard.length} cards)`}
        className="w-14 h-16 rounded-md bg-slate-950/60 border border-white/20 hover:border-slate-400 flex flex-col items-center justify-center text-[10px] text-slate-300"
      >
        <Trash2 className="w-3.5 h-3.5 text-slate-400 mb-0.5" />
        <span className="font-bold">{seat.zones.graveyard.length}</span>
        <span className="text-[7px] uppercase tracking-wider text-slate-400">Graveyard</span>
      </button>

      {isMe && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setTokenPopoverOpen((v) => !v)}
            className="px-2 py-1 rounded-md bg-slate-900 hover:bg-slate-800 border border-slate-700 text-[9px] font-bold text-emerald-300 flex items-center gap-1"
          >
            <Sparkles className="w-2.5 h-2.5" />
            <span>+Token</span>
          </button>

          {tokenPopoverOpen && (
            <div
              style={{ backgroundColor: '#0b101d' }}
              className="absolute right-0 bottom-8 z-[95] w-44 rounded-xl bg-[#0b101d] border border-slate-600 shadow-[0_24px_60px_rgba(0,0,0,0.95)] p-1.5 text-xs flex flex-col gap-1"
            >
              {(seat.availableTokens.length > 0
                ? seat.availableTokens
                : [
                    { name: 'Treasure', typeLine: 'Token Artifact — Treasure', colors: [] },
                    { name: 'Soldier', typeLine: 'Token Creature — Soldier', power: '1', toughness: '1', colors: ['W'] },
                  ]
              ).map((tok) => (
                <button
                  key={tok.name}
                  type="button"
                  onClick={() => {
                    onDispatch({ type: 'CREATE_TOKEN', token: tok, count: 1 });
                    setTokenPopoverOpen(false);
                  }}
                  className="px-2 py-1 rounded hover:bg-slate-800 text-left text-slate-200 flex justify-between"
                >
                  <span>{tok.name}</span>
                  {tok.power && (
                    <span className="text-[10px] text-slate-400">
                      {tok.power}/{tok.toughness}
                    </span>
                  )}
                </button>
              ))}
              {onOpenHelpers && (
                <button
                  type="button"
                  onClick={() => {
                    setTokenPopoverOpen(false);
                    onOpenHelpers('tokens');
                  }}
                  className="px-2 py-1 rounded bg-emerald-950/60 hover:bg-emerald-900 border-t border-slate-800 text-left text-emerald-300 font-bold mt-0.5"
                >
                  Custom / Clone Token...
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderGroupList = (groups: StackedGroup[]) =>
    groups.map((grp) => {
      const isExpanded = expandedGroupKey === grp.key;
      const visibleItems = isExpanded ? grp.items : [grp.items[0]];
      return visibleItems.map((cardItem, idxInGrp) => (
        <CardVisual
          key={cardItem.instanceId}
          item={cardItem}
          sleeveUrl={seat.sleeveUrl}
          accentColor={seat.profileColor}
          size={cardSize}
          stackCount={!isExpanded && idxInGrp === 0 ? grp.items.length : 1}
          reducedMotion={reducedMotion}
          onClick={(e) => {
            e.stopPropagation();
            if (grp.items.length > 1 && e.shiftKey) {
              setExpandedGroupKey(isExpanded ? null : grp.key);
              return;
            }
            if (isMe) {
              onDispatch({ type: 'TAP_CARD', instanceId: cardItem.instanceId });
            } else {
              onInspectCard(cardItem);
            }
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            onOpenCardMenu(cardItem, e);
          }}
          onInspect={() => onInspectCard(cardItem)}
        />
      ));
    });

  const creaturesSection = (
    <div className="flex-1 min-h-[62px] p-1.5 flex flex-col">
      <div className="text-[8px] font-bold uppercase tracking-[0.16em] text-white/35 select-none mb-1">
        Creatures & Permanents
      </div>
      <div className="flex flex-wrap items-center gap-2">{renderGroupList(grouped.frontLine)}</div>
    </div>
  );

  const landsSection = (
    <div className="min-h-[54px] p-1.5 flex flex-col">
      <div className="text-[8px] font-bold uppercase tracking-[0.16em] text-white/35 select-none mb-1">
        Lands
      </div>
      <div className="flex flex-wrap items-center gap-2">{renderGroupList(grouped.landsLine)}</div>
    </div>
  );

  return (
    <section
      aria-label={`${seat.displayName}'s playmat`}
      style={
        seat.playmatUrl
          ? {
              backgroundImage: `linear-gradient(rgba(9,13,22,0.52), rgba(9,13,22,0.74)), url(${seat.playmatUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: focalPointToCssPosition(seat.playmatFocalPoint),
              borderColor: !seatFlag && !isActiveTurn ? seat.matBorderCss : undefined,
            }
          : {
              background: seat.matBgCss,
              borderColor: !seatFlag && !isActiveTurn ? seat.matBorderCss : undefined,
            }
      }
      className={`relative rounded-2xl border transition-colors ${
        seatFlag
          ? seatFlag.status === 'open' && !reducedMotion
            ? 'border-amber-400 flag-pulse-ring'
            : 'border-amber-400 ring-2 ring-amber-400/60'
          : isActiveTurn
          ? 'border-amber-500/80 shadow-[0_0_24px_rgba(245,158,11,0.14)]'
          : 'border-slate-800/90'
      } p-2.5 sm:p-3 flex flex-col gap-2`}
    >
      {/* Seat Name & Status Strip (with non-color-only WCAG AA Seat/Team Badges) */}
      <div className="flex items-center justify-between gap-2 text-xs">
        <div className="flex items-center gap-2 min-w-0 relative">
          <span
            className="px-1.5 py-0.5 rounded bg-slate-950/90 border border-slate-700 font-mono font-black text-[9px] text-slate-200 inline-flex items-center gap-1 shrink-0"
            title={`Seat ${seat.seatIndex + 1}${seat.teamId ? ` (${seat.teamId.toUpperCase()})` : ''}`}
          >
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: seat.profileColor }} />
            <span>S{seat.seatIndex + 1}</span>
            {seat.teamId && (
              <span className="text-violet-300">·T{seat.teamId.replace('team_', '')}</span>
            )}
          </span>
          <span className="font-extrabold tracking-wide text-slate-100 truncate">{seat.displayName}</span>

          {isMe && (
            <button
              type="button"
              aria-label="Seat actions"
              onClick={() => setSeatMenuOpen((o) => !o)}
              className="p-0.5 rounded bg-slate-800/90 hover:bg-slate-700 border border-slate-700 text-slate-300"
            >
              <MoreHorizontal className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Battlefield Seat Actions Menu (Status, Eliminate/Concede) */}
          {isMe && seatMenuOpen && (
            <div
              style={{ backgroundColor: '#0b101d' }}
              className="absolute left-0 top-6 z-[95] w-48 rounded-xl bg-[#0b101d] border border-slate-600 shadow-[0_24px_60px_rgba(0,0,0,0.95)] p-1.5 text-xs space-y-1"
            >
              <div className="px-2 py-0.5 text-[10px] font-bold uppercase text-slate-400">Seat Actions</div>
              {(['ready', 'playing', 'brb', 'afk'] as const).map((st) => (
                <button
                  key={st}
                  type="button"
                  onClick={() => {
                    onDispatch({ type: 'SET_SEAT_STATUS', status: st });
                    setSeatMenuOpen(false);
                  }}
                  className="w-full text-left px-2 py-1 rounded hover:bg-slate-800 flex justify-between"
                >
                  <span className="uppercase">{st}</span>
                  {seat.status === st && <span className="text-emerald-400 font-bold">✓</span>}
                </button>
              ))}
              {onOpenCosmetics && (
                <button
                  type="button"
                  onClick={() => {
                    onOpenCosmetics();
                    setSeatMenuOpen(false);
                  }}
                  className="w-full text-left px-2 py-1 rounded hover:bg-slate-800 text-amber-300 flex items-center gap-1.5 border-t border-slate-800 pt-1.5"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Playmat & Card Sleeves</span>
                </button>
              )}
              {onOpenPaperMode && (
                <button
                  type="button"
                  onClick={() => {
                    onOpenPaperMode();
                    setSeatMenuOpen(false);
                  }}
                  className="w-full text-left px-2 py-1 rounded hover:bg-slate-800 text-emerald-300 flex items-center gap-1.5"
                >
                  <Camera className="w-3.5 h-3.5" />
                  <span>Paper & Webcam Mode</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  onDispatch({
                    type: 'ELIMINATE_PLAYER',
                    targetSeatId: seat.seatId,
                    reason: 'concede',
                  });
                  setSeatMenuOpen(false);
                }}
                className="w-full text-left px-2 py-1 rounded hover:bg-rose-950 text-rose-300 flex items-center gap-1.5 border-t border-slate-800 pt-1.5"
              >
                <Skull className="w-3.5 h-3.5" />
                <span>Mark Eliminated / Concede</span>
              </button>
            </div>
          )}

          {isActiveTurn && (
            <span className="px-1.5 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/60 text-amber-300 text-[10px] font-bold">
              TURN
            </span>
          )}

          {seat.streamMode && (
            <span className="px-1.5 py-0.5 rounded-full bg-rose-500/20 border border-rose-400/60 text-rose-300 text-[10px] font-bold flex items-center gap-1">
              <Radio className="w-2.5 h-2.5" />
              LIVE
            </span>
          )}

          {seat.paperMode?.enabled && (
            <button
              type="button"
              onClick={() => isMe && onOpenPaperMode?.()}
              className="px-1.5 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/60 text-emerald-200 text-[10px] font-bold flex items-center gap-1"
              title="Paper & Webcam Hybrid Seat (Digital Twin Active)"
            >
              <Camera className="w-2.5 h-2.5" />
              <span>PAPER TWIN</span>
            </button>
          )}

          {seatFlag && (
            <span className="px-2 py-0.5 rounded-full bg-amber-500 text-slate-950 font-extrabold text-[10px]">
              WAIT #{seatFlag.order} ({seatFlag.reason})
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0 text-[11px]">
          {/* Floating Mana Pool Strip */}
          {Object.entries(seat.manaPool).some(([, qty]) => qty > 0) && (
            <button
              type="button"
              onClick={() => isMe && onOpenHelpers?.('counters')}
              title="Floating Mana Pool"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-950/90 border border-amber-400/50 text-[10px] font-mono text-amber-200"
            >
              {(['W', 'U', 'B', 'R', 'G', 'C'] as const)
                .filter((col) => (seat.manaPool[col] ?? 0) > 0)
                .map((col) => (
                  <span key={col}>
                    {seat.manaPool[col]}
                    {col}
                  </span>
                ))}
            </button>
          )}

          {seat.activePrivateAction && (
            <span className="px-2 py-0.5 rounded bg-indigo-950/90 border border-indigo-400/60 text-indigo-200 text-[10px] flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {seat.activePrivateAction.kind === 'scry'
                ? `Scrying ${seat.activePrivateAction.count}`
                : seat.activePrivateAction.kind === 'search_library'
                ? 'Searching Library'
                : `Top ${seat.activePrivateAction.count}`}
            </span>
          )}

          {seat.deckMeta && (
            <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-900/90 border border-slate-800 text-[10px] text-slate-400">
              <span>B{seat.deckMeta.detectedBracket}</span>
              <span>·</span>
              <span>{seat.deckMeta.mainCount + seat.deckMeta.commanderNames.length}c</span>
              {seat.deckMeta.showTransparency && (
                <>
                  <span>·</span>
                  <span className="text-amber-300">{seat.deckMeta.gameChangerCount} GC</span>
                </>
              )}
            </span>
          )}

          <span
            data-zone-anchor={!isMe ? `${seat.seatId}:hand` : undefined}
            className="px-2 py-0.5 rounded bg-slate-900/90 border border-slate-700/80 font-semibold text-slate-200"
          >
            Hand {seat.zones.handCount}
          </span>

          {isMe && onOpenDeckLoader && (
            <button
              type="button"
              onClick={onOpenDeckLoader}
              className="px-2 py-0.5 rounded bg-indigo-600/30 hover:bg-indigo-600/50 border border-indigo-400/50 text-indigo-100 text-[10px] font-semibold flex items-center gap-1"
            >
              <Upload className="w-3 h-3" />
              <span>Select Deck</span>
            </button>
          )}
        </div>
      </div>

      {/* Ultra PRO 3-Column Mat Geometry */}
      <div className="grid grid-cols-[auto_1fr_auto] gap-2.5 sm:gap-3 items-stretch">
        {mirrored ? libraryAndGraveRail : lifeAndExileRail}

        <div
          data-zone-anchor={`${seat.seatId}:battlefield`}
          className="relative rounded-lg border border-white/20 bg-slate-950/30 flex flex-col justify-between min-w-0 divide-y divide-white/15 overflow-hidden"
        >
          {seat.paperMode?.enabled && lowBandwidthStillUrl && (
            <img
              src={lowBandwidthStillUrl}
              alt={`${seat.displayName} paper playmat still`}
              className="absolute inset-0 w-full h-full object-cover opacity-35 pointer-events-none"
            />
          )}

          {seat.paperMode?.videoMarkers && seat.paperMode.videoMarkers.length > 0 && (
            <div className="absolute inset-0 pointer-events-none z-20">
              {seat.paperMode.videoMarkers.map((mk) => (
                <button
                  key={mk.markerId}
                  type="button"
                  onClick={() => {
                    if (!isMe) return;
                    onDispatch({ type: 'CLEAR_VIDEO_MARKER', markerId: mk.markerId });
                  }}
                  style={{ left: `${mk.xPct}%`, top: `${mk.yPct}%` }}
                  title={isMe ? 'Click to clear pinned video marker' : mk.label}
                  className="pointer-events-auto -translate-x-1/2 -translate-y-1/2 absolute px-1.5 py-0.5 rounded-full bg-cyan-500/90 border border-white/80 text-slate-950 font-black text-[9px] shadow-lg"
                >
                  {mk.label}
                  {mk.count !== undefined && mk.count > 0 ? ` ×${mk.count}` : ''}
                </button>
              ))}
            </div>
          )}

          {mirrored ? (
            <>
              {landsSection}
              {creaturesSection}
            </>
          ) : (
            <>
              {creaturesSection}
              {landsSection}
            </>
          )}
        </div>

        {mirrored ? lifeAndExileRail : libraryAndGraveRail}
      </div>
    </section>
  );
}
